import { OrangeTextButton } from "../../common/OrangeTextButton";
import { DayField } from "./DayField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePickerField } from "./TimePickerField";
import { DatePickerField } from "./DatePickerField";
import { LocationDuskToDawnField } from "./LocationDuskToDawnField";
import { BufferTimeField } from "./BufferTimeField";
import { SwitchField } from "./SwitchField";
import { handleAddAnotherSchedule } from "./handleAddAnotherSchedule";
import { handleSubmitSchedule } from "./handleSubmitSchedule";
import React from "react";
import { StatusNotificationScreen } from "../../common/StatusNotificationScreen";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Backdrop, Checkbox } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { handleCreateDaysArray } from "./handleCreateDaysArray";
import dayjs from "dayjs";
import ErrorBoundary from "../../../ErrorBoundary";
import { ScheduleGrid } from "../Sites/SiteAboutPage/ScheduleGrid";
import { handleConvertSchedulesToBooleanArrays } from "../Sites/SiteAboutPage/SiteAboutPageUtils/handleConvertSchedulesToBooleanArrays";
import { handleCreateDuskToDawn } from "../Sites/SiteAboutPage/SiteAboutPageUtils/handleCreateDuskToDawn";
import { handleCreateDuskToDawnLocationPlusBuffer } from "../Sites/SitesPage/SitesPageUtils/handleCreateDuskToDawnLocationPlusBuffer";
import { ActuateButton } from "../../common/ActuateButton";
import { EditScheduleTimesDialog } from "../Sites/SiteAboutPage/EditScheduleTimesDialog";
import { CalendarsTableAddSchedule } from "./CalendarsTableAddSchedule";
import { CalendarInfoDialog } from "./CalendarInfoDialog";
import { siteArmButtonDisableCheckRefresh } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteArmButtonDisableCheckRefresh";
import { useSiteAboutStore } from "../Sites/SiteAboutPage/siteAboutStore/siteAboutStore";
import { temporaryUpdateScheduleData } from "../Sites/SiteAboutPage/SiteAboutPageUtils/temporaryUpdateScheduleData";
import { handleCreateInitialTimeSchedules } from "../Sites/SiteAboutPage/SiteAboutPageUtils/handleCreateInitialTimeSchedules";

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const ScheduleScreen = ({
  setVisibleScreen,
  visibleScreen,
  setLargerScreen,
}: {
  setVisibleScreen: any;
  visibleScreen: any;
  setLargerScreen: any;
}) => {
  const locations = JSON.parse(localStorage.locations) || [];

  const locationIds = new Map();

  locations.forEach((element: any) => {
    locationIds.set(`${element.name}, ${element.region}`, element.id);
  });

  const navigate = useNavigate();

  const {
    setScheduleGridEditable,
    setSiteSchedules,
    setTimeRanges,
    setGrid,
    setDuskToDawn,
    setDuskToDawnLocation,
    editTimeScheduleDialogOpen,
    siteSchedules,
  } = useSiteAboutStore();

  const emptySchedule = temporaryUpdateScheduleData([
    {
      customer: localStorage.siteId,
      start_time: null,
      end_time: "00:00:00",
      always_on: false,
      day_of_week: ["5", "6", "2", "4", "3", "1", "0"],
      schedule_status: true,
      is_override: false,
      location_dusk_dawn: null,
      enabled: true,
      override_start_date: null,
      override_end_date: null,
      buffer_time: null,
    },
  ]);

  const calendarsData = JSON.parse(localStorage.calendarsData).results.filter(
    (calendar: any) => calendar.group.name === localStorage.groupSelected
  );

  const [calendarInfoDialogOpen, setCalendarInfoDialogOpen] =
    React.useState(false);

  const [selectedRow, setSelectedRow] = React.useState<any>({});

  const [calendarEvents, setCalendarEvents] = React.useState([]);

  const [editingCalendar] = React.useState(false);

  const [loadingEventData, setLoadingEventData] = React.useState(false);

  const [selectedCalendars, setSelectedCalendars] = React.useState([]);

  const [addScheduleError, setAddSchedulError] = React.useState(
    localStorage.addScheduleError
  );

  const navigateSitesPage = () => {
    navigate(`/sites/aboutsite/${localStorage.siteId}`);
  };

  React.useEffect(() => {
    setScheduleGridEditable(true);
    setSiteSchedules(emptySchedule);
    setTimeRanges(handleCreateInitialTimeSchedules(emptySchedule[0].schedule));
    setGrid(handleConvertSchedulesToBooleanArrays(emptySchedule[0].schedule));
    setDuskToDawn(handleCreateDuskToDawn(emptySchedule[0].schedule));
    setDuskToDawnLocation(
      handleCreateDuskToDawnLocationPlusBuffer(emptySchedule[0].schedule)
    );
  }, []);

  return (
    <ErrorBoundary>
      <div>
        {editTimeScheduleDialogOpen && (
          <EditScheduleTimesDialog
            setActiveTab={() => {}}
            setEditMode={() => {}}
          />
        )}
        {addScheduleError && (
          <div className="absolute w-[600px] h-[50px]">
            <Alert
              severity="error"
              onClose={() => {
                setAddSchedulError(false);
                localStorage.removeItem("addScheduleError");
              }}
            >
              <p className="font-mulish font-bold text-[16px]">
                Problem adding Schedule. Please try again.
              </p>
            </Alert>
          </div>
        )}
        {calendarInfoDialogOpen && (
          <div className="absolute">
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={calendarInfoDialogOpen}
            >
              <CalendarInfoDialog
                setCalendarInfoDialogOpen={setCalendarInfoDialogOpen}
                selectedRow={selectedRow}
                calendarEvents={calendarEvents}
                setCalendarEvents={setCalendarEvents}
                editingCalendar={editingCalendar}
                loadingEventData={loadingEventData}
              />
            </Backdrop>
          </div>
        )}
        <p
          className={
            "font-mulish absolute top-[70px] text-[#283E6D] right-[1070px] text-[21px]"
          }
        >
          Please create a schedule for this Site
        </p>
        <div className="pt-[120px] pl-[30px]">
          <ScheduleGrid />
          {calendarsData.length > 0 && (
            <div>
              <p
                className={
                  "font-mulish font-bold text-[#283E6D] pt-[10px] text-[16px]"
                }
              >
                {`Calendars (optional)`}
              </p>
              <div className="pt-[10px]">
                <CalendarsTableAddSchedule
                  calendarsData={calendarsData}
                  setLoadingEventData={setLoadingEventData}
                  setSelectedRow={setSelectedRow}
                  setCalendarEvents={setCalendarEvents}
                  navigate={navigate}
                  setShowMoreDialogOpen={setCalendarInfoDialogOpen}
                  selectedCalendars={selectedCalendars}
                  setSelectedCalendars={setSelectedCalendars}
                />
              </div>
            </div>
          )}
        </div>
        <div className="absolute right-[50px] top-[70px]">
          <ActuateButton
            onClick={() => {
              setLargerScreen(false);
              handleSubmitSchedule(
                siteSchedules,
                setVisibleScreen,
                visibleScreen,
                setLargerScreen,
                navigate,
                navigateSitesPage,
                selectedCalendars
              );
              setVisibleScreen(
                <StatusNotificationScreen
                  styling="absolute first-line:font-black font-mulish text-[20px] right-[150px] text-center text-[#283E6D] top-80"
                  text={
                    <p>
                      We&apos;re submitting this information.
                      <br />
                      This might take a few seconds.
                    </p>
                  }
                />
              );
            }}
            text="Submit"
            disabled={false}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
};
